import type { MouseEventHandler } from 'react';
import React from 'react';
import styled from 'styled-components';
import type { TextLinkProps } from '@oneaudi/unified-web-components';
import { TextLink } from '@oneaudi/unified-web-components';
import {
  getClassNamesForLinks,
  getIdNameForLink,
  getLinkTarget,
  onCookieModalLinkClick,
} from '../../services/helpers';
import type { Link } from '../../types/audi-footer-response.types';
import type { TrackingClickEvent } from '../../utils/tracking';
import { useTracking } from '../../utils/tracking';
import { TrackingContext } from '../../context';
import { responsiveStyles } from '../../utils/responsiveStyles';
import { breakpoints } from '../../utils/breakpoints';

type LinksProps = {
  id: string;
  countryCode?: string;
  categoryIndex: string;
  headline: string;
  links: Link[];
  useOneLayer?: boolean;
  openLayer: MouseEventHandler;
  isOpen: boolean;
};

type AccessibleTextLinkProps = TextLinkProps & {
  tabIndex?: number;
};
// eslint-disable-next-line react/display-name
const AccessibleTextLink = React.forwardRef<any, AccessibleTextLinkProps>(
  ({ tabIndex, ...props }, ref) => (
    <TextLink {...props} {...(tabIndex !== undefined && { tabIndex })} ref={ref} />
  ),
);

const Links: React.FC<LinksProps> = ({
  id,
  categoryIndex,
  headline,
  countryCode,
  links,
  useOneLayer,
  openLayer,
  isOpen,
}) => {
  const trackingService = React.useContext(TrackingContext);
  const { sendClickEvent } = useTracking(trackingService);
  const sendButtonClickEvent = (
    label: string,
    url: string,
    variant: string,
    openin: string,
    position: string,
  ) => {
    const event: TrackingClickEvent = {
      linkLabel: label,
      linkUrl: url,
      elementName: variant,
      headline,
      OpenIn: openin,
      position: `${categoryIndex}-${position}`,
    };

    sendClickEvent(event);
  };
  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    link: Link,
    index: string,
  ): void => {
    const target = getLinkTarget(link, useOneLayer);
    const { Type, Text, Url, Target } = link;
    if (!!Type && Type === 'cookie-settings-layer' && countryCode !== 'US') {
      onCookieModalLinkClick(event);
    } else if (Target === '_layer' && useOneLayer) {
      openLayer(event);
    }
    sendButtonClickEvent(Text, Url, 'text link', target, index);
  };
  return (
    <FooterLinksWrapperStyled id={id} role="region" aria-labelledby={id}>
      <ul>
        {links.map((link, i) => {
          const randomNumber = Math.floor(Math.random() * 1000) + 1;
          return (
            <li key={`${randomNumber}${link.Target}${link.Text}${link.Url}`}>
              <AccessibleTextLink
                className={getClassNamesForLinks(link, countryCode, useOneLayer)}
                onClick={(event) => {
                  handleClick(event, link, (i + 1).toString());
                }}
                href={link.Url}
                rel="noreferrer"
                size="sm"
                variant="standalone"
                target={getLinkTarget(link, useOneLayer)}
                tabIndex={isOpen ? 0 : -1}
                {...(link.AriaLabel ? { 'aria-label': link.AriaLabel } : {})}
                {...(getIdNameForLink(link) ? { id: getIdNameForLink(link) } : {})}
              >
                {link.Text}
              </AccessibleTextLink>
            </li>
          );
        })}
      </ul>
    </FooterLinksWrapperStyled>
  );
};
const FooterLinksWrapperStyled = styled.div`
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  ${(): string =>
    responsiveStyles(
      {
        'background-color': { m: 'var(--one-footer-color-black)' },
        height: { m: 'auto' },
        margin: { m: '0' },
        'max-height': { m: 'initial' },
        opacity: { m: '1' },
      },
      breakpoints,
    )}

  & > ul {
    padding: calc(var(--one-footer-space-l) - var(--one-footer-space-m) / 2) 0;
    /* stylelint-disable */
    ${(): string =>
      responsiveStyles(
        {
          padding: { m: '0' },
        },
        breakpoints,
      )}

    & > li {
      padding: 0 var(--one-footer-side-spacing);
      ${(): string =>
        responsiveStyles(
          {
            padding: { m: '0 0 var(--one-footer-space-s)' },
          },
          breakpoints,
        )}
      /* stylelint-enable */
      & > a {
        display: block;
        padding: calc(var(--one-footer-space-m) / 2) 0;
        width: 100%;
        ${(): string =>
          responsiveStyles(
            {
              display: { m: 'inline' },
              padding: { m: '0' },
              width: { m: 'auto' },
            },
            breakpoints,
          )}
      }
    }

    & > li:last-of-type {
      ${(): string =>
        responsiveStyles(
          {
            padding: { m: '0' },
          },
          breakpoints,
        )}
    }
  }

  @media (min-width: 768px) {
    padding: 3px; // adding some space for focus outline
  }
`;

export default Links;
